// IndentEntries.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import IndentEntryDataService from '../services/IndentEntryService';
import InvoicePDF from './InvoicePDF'; // Import the InvoicePDF component
import { formatNumberKGPlain } from './utils';

const IndentEntries = () => {
  const { code: indentCode } = useParams();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const response = await IndentEntryDataService.findIndentEntries(indentCode);
        setEntries(response.data);
      } catch (error) {
        console.error('Error fetching entries:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEntries();
  }, [indentCode]);

  const firstEntry = entries?.[0] || null;
  const comment = firstEntry?.indent?.comment || '';
  const indentType = firstEntry?.indent?.indentType || '';
  const customer = firstEntry?.customer || null;
  const totalWeight = entries?.reduce((acc, item) => acc + item.articleWeightForGivenQuantity, 0) || 0;
  const totalRealQuantity = entries?.reduce((total, entry) => total + (entry.realQuantity ? parseFloat(entry.realQuantity) : 0), 0) || 0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header Section */}
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-800 flex items-center">
          <i className="fas fa-file-invoice mr-3 text-blue-500"></i>
          Porudžbina {indentCode}
        </h3>
      </div>

      {/* Customer Information Card */}
      {customer && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h4 className="text-xl font-semibold text-gray-700 mb-4">
            Informacije o kupcu
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600">
            <div>
              <p className="mb-2">
                <span className="font-semibold">Naziv:</span> {customer.nameOfTheLegalEntity}
              </p>
              <p className="mb-2">
                <span className="font-semibold">Kontakt osoba:</span> {customer.contactPerson}
              </p>
              <p className="mb-2">
                <span className="font-semibold">Broj telefona:</span> {customer.contactNumber}
              </p>
            </div>
            <div>
              <p className="mb-2">
                <span className="font-semibold">Email:</span> {customer.email}
              </p>
              <p className="mb-2">
                <span className="font-semibold">Adresa:</span> {customer.address}, {customer.city}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Type Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h4 className="text-xl font-semibold text-gray-700 mb-4">Način plaćanja</h4>
        <p className="text-gray-600">
          {indentType
            ? indentType === 'GOTOVINSKI_RACUN'
              ? 'GOTOVINSKI RAČUN'
              : indentType.replace(/_/g, ' ')
            : 'Nije dostupno'}
        </p>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Šifra proizvoda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Artikal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jedinica Mere</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Količina</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Težina</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry, index) => (
                <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.article.code || "—"}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.article.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.article.unitOfMeasurement}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.requestedQuantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumberKGPlain(entry.articleWeightForGivenQuantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comment Section */}
      {comment && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h4 className="text-xl font-semibold text-gray-700 mb-4">Napomena</h4>
          <p className="text-gray-600 whitespace-pre-wrap">{comment}</p>
        </div>
      )}

      {/* Totals Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-700">Ukupna Težina:</span>
          <span className="text-lg font-bold text-gray-900">
            {formatNumberKGPlain(totalWeight)}
          </span>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-end">
        <InvoicePDF
          indentCode={indentCode}
          entries={entries}
          indentType={indentType}
          customer={customer}
          totalWeight={totalWeight}
          totalRealQuantity={totalRealQuantity}
          comment={comment}
        />
      </div>
    </div>
  );
};

export default IndentEntries;
